<script setup lang="ts">
import { computed, ref } from "vue";
import { marked } from "marked";

import { Booking, Desk } from "../data/types";
import {
  useContextStore,
  usePeopleStore,
  useToastStore,
} from "../data/store.ts";
import {
  cloneBooking,
  d2s,
  DELETE,
  POST,
  preparePayload,
} from "../data/api.ts";
import { meetingRoomLookup, meetingRooms } from "../data/rooms.ts";

const peopleStore = usePeopleStore();
const contextStore = useContextStore();
const toaster = useToastStore();

const emit = defineEmits(["clear", "saved", "edited", "deleted", "canceled"]);
defineExpose({
  prefillNew,
  fillExisting,
  clear,
});

const info = ref<Booking | null>(null);
const isNew = ref(false);
const seriesInfo = ref({
  repeat: 1,
  weeks: 1,
});

const modalVisible = computed(() => info.value !== null);
const bookee = computed(
  () => (peopleStore.people ?? {})[info.value?.person ?? ""],
);
const isOwn = computed(() => contextStore.user === bookee.value.id);
const roomInfo = computed<Desk | null>(() => ((info.value && info.value.desk) ? meetingRoomLookup[info.value.desk] ?? null : null));

function prefillNew({
                      begin,
                      end,
                      desk,
                    }: {
  begin: Date;
  end: Date;
  desk: number;
}) {
  isNew.value = true;
  info.value = {
    id: crypto.randomUUID(),
    day: begin,
    person: contextStore.user as string,
    type: "MEETING",
    //series?: string;
    name: "",
    note: "",
    desk,
    begin,
    end,
  };
  seriesInfo.value = {
    repeat: 1,
    weeks: 1,
  };
}

function fillExisting(booking: Booking) {
  isNew.value = false;
  info.value = booking;
}

function clearData() {
  info.value = null;
  seriesInfo.value = {
    repeat: 1,
    weeks: 1,
  };
}

function clear() {
  clearData();
  emit("clear");
  emit("canceled");
}

function save() {
  if (info.value) {
    if (isNew.value) {
      if (seriesInfo.value.repeat < 2) {
        POST("/booking/", preparePayload(info.value))
          .then(() => {
            toaster.toast("Saved!", "SUCC");
            clearData();
            emit("saved", info.value);
          })
          .catch(() => {
            toaster.toast("Not saved!", "FAIL");
          });
      } else {
        info.value.series = crypto.randomUUID();
        const bookings = [...new Array(seriesInfo.value.repeat)].map((_, i) => {
          const booking = cloneBooking(info.value as Booking);
          if (i > 0) booking.id = crypto.randomUUID();
          booking.day.setDate(
            booking.day.getDate() + 7 * seriesInfo.value.weeks * i,
          );
          booking.begin?.setDate(
            booking.begin.getDate() + 7 * seriesInfo.value.weeks * i,
          );
          booking.end?.setDate(
            booking.end.getDate() + 7 * seriesInfo.value.weeks * i,
          );
          return booking;
        });
        POST(
          "/bookings/",
          bookings.map((b: Booking) => preparePayload(b)),
        )
          .then(() => {
            toaster.toast("Saved series!", "SUCC");
            clearData();
            emit("saved", bookings[0]);
          })
          .catch(() => {
            toaster.toast("Not saved!", "FAIL");
          });
      }
    } else {
      // is not new / edit existing
      POST("/booking/edit", preparePayload(info.value))
        .then(() => {
          toaster.toast("Updated!", "SUCC");
          clearData();
          emit("edited", info.value);
        })
        .catch(() => {
          toaster.toast("Not updated!", "FAIL");
        });
    }
  }
}

function deleteOccurrence() {
  if (info.value) {
    DELETE("/booking/", { bid: info.value.id })
      .then(() => {
        toaster.toast(`Deleted "${info.value?.name}"`);
        clearData();
        emit("deleted");
      })
      .catch(() => toaster.toast("Failed deletion", "FAIL"));
  }
}

function deleteFuture() {
  if (info.value) {
    DELETE("/booking/", { series: info.value.series as string, after: d2s(info.value.day) })
      .then(() => {
        toaster.toast(`Deleted "${info.value?.name}"`);
        clearData();
        emit("deleted");
      })
      .catch(() => toaster.toast("Failed deletion", "FAIL"));
  }
}

function deleteSeries() {
  if (info.value) {
    DELETE("/booking/", {
      series: info.value.series as string,
      after: d2s(info.value.day),
    })
      .then(() => {
        toaster.toast(`Deleted "${info.value?.name}"`);
        clearData();
        emit("deleted");
      })
      .catch(() => toaster.toast("Failed deletion", "FAIL"));
  }
}

function parseTimeInput(newTime: string) {
  const ret = new Date(newTime);
  // clip to nearest :10 :20... minute mark.
  ret.setMinutes(Math.round(ret.getMinutes() / 5) * 5);
  //ret.setMinutes(ret.getMinutes() + ret.getTimezoneOffset())
  return ret;
}

function asTimeLocal(dt: Date) {
  const ret = new Date(dt);
  ret.setMinutes(ret.getMinutes() - ret.getTimezoneOffset());
  return ret.toISOString().slice(0, 16);
}
</script>

<template>
  <div v-if="info" class="modal" style="display: block" @click.stop="clear">
    <div class="modal-dialog modal-lg modal-dialog-centered" @click.stop>
      <div class="modal-content text-start">
        <!--<div class="modal-header">-->
        <!--  <h1 class="modal-title fs-5" id="exampleModalLabel">Modal title</h1>-->
        <!--  <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>-->
        <!--</div>-->

        <template v-if="isOwn">
          <div class="modal-body">
            <div class="row mb-3">
              <div class="col">
                <label for="sessionTitle">Descriptive title</label>
                <input
                  id="sessionTitle"
                  v-model="info.name"
                  type="text"
                  class="form-control"
                />
              </div>
              <!--          </div>-->

              <!--          <div class="row text-start mb-3 ">-->
              <!--            Series <span class="small">(only fill if needed)</span>-->
              <div v-if="isNew && seriesInfo.repeat > 1" class="col-2">
                <label for="nweeks">Every N weeks</label>
                <input
                  id="nweeks"
                  v-model="seriesInfo.weeks"
                  class="form-control"
                  type="number"
                />
              </div>
              <div v-if="isNew" class="col-2">
                <label for="repeats">Occurrences</label>
                <input
                  id="repeats"
                  v-model="seriesInfo.repeat"
                  class="form-control"
                  min="1"
                  type="number"
                />
              </div>
            </div>
            <div class="mb-3">
              <label for="commentText" class="form-label">Info</label>
              <textarea
                id="commentText"
                v-model="info.note"
                class="form-control"
                rows="3"
              ></textarea>
            </div>

            <div class="row text-start mb-3">
              <div class="col">
                <div class="small">Room</div>
                <div>
                  <select class="form-select" aria-label="Room" v-model="info.desk">
                    <option v-for="room in meetingRooms" :value="room.id" :key="room.id">{{ room.name }}</option>
                  </select>
                </div>
              </div>
              <div class="col">
                <div class="small">Booked by</div>
                <div v-if="bookee">{{ bookee.name }} ({{ bookee.group }})</div>
                <div v-else>[unknown]</div>
              </div>
              <div class="col">
                <div class="small">Start</div>
                <div v-if="info.begin">
                  <!-- @vue-ignore -->
                  <input
                    type="datetime-local"
                    :value="asTimeLocal(info.begin)"
                    @input="
                      (event) => {
                        if (info)
                          info.begin = parseTimeInput(event.target?.value);
                      }
                    "
                  />
                </div>
                <div class="text-muted small">
                  {{
                    info.begin?.toLocaleString(undefined, {
                      dateStyle: "short",
                      timeStyle: "short",
                    })
                  }}
                </div>
              </div>
              <div class="col">
                <div class="small">End</div>
                <div v-if="info.end">
                  <!-- @vue-ignore -->
                  <input
                    type="datetime-local"
                    :value="asTimeLocal(info.end)"
                    @input="
                      (event) => {
                        if (info)
                          info.end = parseTimeInput(event.target?.value);
                      }
                    "
                  />
                </div>
                <div class="text-muted small">
                  {{
                    info.end?.toLocaleString(undefined, {
                      dateStyle: "short",
                      timeStyle: "short",
                    })
                  }}
                </div>
              </div>
            </div>

            <template v-if="!isNew">
              <template v-if="info.series">
                <div class="row">
                  <div class="col text-muted small">
                    <i class="bi-collection me-1" />Calendar series
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger me-1"
                      @click="deleteOccurrence"
                    >
                      Delete occurrence
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger me-1"
                      @click="deleteSeries"
                    >
                      Delete series (all)
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger me-1"
                      @click="deleteFuture"
                    >
                      Delete series (future)
                    </button>
                  </div>
                </div>
              </template>

              <template v-else>
                <div class="row">
                  <div class="col">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger"
                      @click="deleteOccurrence"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </template>
            </template>
          </div>
          <div class="modal-footer p-1">
            <button
              type="button"
              class="btn btn-sm btn-secondary"
              @click="clear"
            >
              Cancel
            </button>
            <button type="button" class="btn btn-sm btn-primary" @click="save">
              Save
            </button>
          </div>
        </template>

        <template v-else>
          <div class="modal-body">
            <div class="row text-start mb-3">
              <div class="col">
                <i v-if="info.series" class="bi-collection me-1" />
                <strong>{{ info.name }}</strong>
              </div>
            </div>
            <div class="row text-start mb-3">
              <div class="col-auto" v-html="marked((info.note) ? info.note : '_[NO DESCRIPTION]_')"></div>
            </div>
            <div class="row text-start mb-3">
              <div class="col" v-if="roomInfo">
                <div class="small">Room</div>
                <div>{{ roomInfo.name }}</div>
              </div>
              <div class="col">
                <div class="small">Booked by</div>
                <div v-if="bookee">{{ bookee.name }} ({{ bookee.group }})</div>
                <div v-else>[unknown]</div>
              </div>
              <div class="col">
                <div class="small">Start</div>
                <div>
                  {{
                    info.begin?.toLocaleString(undefined, {
                      dateStyle: "short",
                      timeStyle: "short",
                    })
                  }}
                </div>
              </div>
              <div class="col">
                <div class="small">End</div>
                <div>
                  {{
                    info.end?.toLocaleString(undefined, {
                      dateStyle: "short",
                      timeStyle: "short",
                    })
                  }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
  <div v-show="modalVisible" class="modal-backdrop fade show"></div>
</template>

<style scoped></style>
